import { useEffect, useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type AppRole = "admin" | "account_manager" | "partner";

interface RoleState {
  userId: string | null;
  roles: AppRole[];
  loading: boolean;
}

let state: RoleState = { userId: null, roles: [], loading: true };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const setState = (patch: Partial<RoleState>) => {
  state = { ...state, ...patch };
  emit();
};

let inflightUserId: string | null = null;

const fetchRoles = async (userId: string) => {
  if (inflightUserId === userId) return;
  inflightUserId = userId;
  setState({ loading: true });
  try {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (state.userId === userId || userId === state.userId) {
      setState({
        userId,
        roles: (data ?? []).map((r) => r.role as AppRole),
        loading: false,
      });
    }
  } finally {
    if (inflightUserId === userId) inflightUserId = null;
  }
};

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};
const getSnapshot = () => state;

export function useUserRole() {
  const { user, loading: authLoading } = useAuth();
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      if (state.userId !== null || state.roles.length || state.loading) {
        setState({ userId: null, roles: [], loading: false });
      }
      return;
    }
    if (state.userId !== user.id) {
      setState({ userId: user.id, roles: [], loading: true });
      fetchRoles(user.id);
    }
  }, [user, authLoading]);

  const roles = snap.userId === user?.id ? snap.roles : [];
  const isAdmin = roles.includes("admin");
  const isAccountManager = roles.includes("account_manager");
  const isStaff = isAdmin || isAccountManager;

  return {
    roles,
    isAdmin,
    isAccountManager,
    isStaff,
    loading: authLoading || snap.loading || snap.userId !== user?.id,
  };
}
